import { ApiEndpointQuery, PrefetchOptions } from '@reduxjs/toolkit/dist/query/core/module'
import { createApi, fetchBaseQuery, retry } from '@reduxjs/toolkit/query/react'

import { HYDRATE } from 'next-redux-wrapper'
import React from 'react'
import { useDispatch } from 'react-redux'

/**
 * Classy Embedded proxies calls upstream through the NextJS service layer.
 *
 * Therefore, our baseUrl should point to the NextJS service.
 */
const baseQuery = fetchBaseQuery({
  /**
   * TODO: We'll likely want to get baseUrl dynamically since domain may change
   * for domain masked organizations.
   */
  baseUrl: `/embedded/api`,
})

const baseQueryWithRetry = retry(baseQuery, {
  retryCondition: (error, args, extraOptions) => {
    // Maximum of three attempts
    if (extraOptions.attempt > 3) return false

    // Retry only GET requests
    return args.method && args.method.toUpperCase() === 'GET'
  },
})

/**
 * Creates a base API to inject endpoints into.
 * Components using this API should import from the injected site,
in order to get the appropriate types,
 * and to ensure that the file injecting the endpoints is loaded
 */
export const api = createApi({
  /**
   * Base query with the retry enhancer.
   */
  baseQuery: baseQueryWithRetry,
  /**
   * Tag types must be defined in the original API definition
   * for any tags that would be provided by injected endpoints
   */
  tagTypes: [],
  /**
   * This api has endpoints injected by adjacent services,
   * which is why no endpoints are shown below.
   */
  endpoints: () => ({}),
  /**
   * This function is used for server-side rendering (SSR) in Redux Toolkit.
   * It extracts the relevant information from the `HYDRATE` action dispatched
   * during server rendering and applies it to the corresponding slice reducer,
   * allowing the initial state of the Redux store to be hydrated with data from the server.
   * This improves overall application performance and provides a better user experience.
   * The extracted data is used to rehydrate the store state on the client.
   *
   * @param action - The current Redux action being processed.
   * @param reducerPath - The path to the slice reducer for which to extract rehydration data.
   * @returns The payload data for the corresponding slice reducer, if the action type is `HYDRATE`.
   */
  extractRehydrationInfo(action, { reducerPath }) {
    if (action.type === HYDRATE) {
      return action.payload[reducerPath]
    }
  },
})

// this name is dumb but the linter wants hooks to start with 'use'
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function usePrefetchNow<T extends ApiEndpointQuery<any, any>>(
  endpoint: T,
  arg: unknown,
  condition = true,
  options: PrefetchOptions = {},
) {
  const dispatch = useDispatch()

  React.useEffect(() => {
    if (condition) {
      // @ts-expect-error endpoint names are not yet defined on 'api'
      dispatch(api.util.prefetch(endpoint.name, arg, options))
    }
  }, [arg, condition, dispatch, endpoint.name, options])
}

export const {
  util: { getRunningQueriesThunk },
} = api
